import React, {ReactNode, useEffect, useMemo, useState} from 'react';
import './NewAssignment.scss';
import ArrButton from '../../common/arr-button/ArrButton';
import {Box, Tab, Tabs, Typography} from '@mui/material';
import {ArrDrawer} from '../../common/arr-drawer/ArrDrawer';
import {
  FIRST_LAST_NAME_REGEX,
  ToastType,
  transition_time_default,
} from '../../../shared/constants';
import ManagePermissions from './manage-permissions/ManagePermissions';
import {
  useCreateAssignment,
  useCreateLandingData,
  useEditAssignmentById,
  useEditAssignmentGroup,
  useGetAssignmentGroupById,
} from '../../../hooks/assignments/useAssignments';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import {ReactComponent as LeftChevron} from '../../../assets/images/left-chevron.svg';
import SelectSortCodingForms from './select-sort-coding-forms/SelectSortCodingForms';
import {t} from 'i18next';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import {useNavigate} from 'react-router-dom';
import Metadata from './metadata/Metadata';
import {useDispatch, useSelector} from 'react-redux';
import {
  setClassification,
  setFormData,
  setListData,
  setPriority,
  clearBulkAssignFlowData,
  setSelectedData,
  clearBackSourceTabIndex,
} from '../../../redux/slice/assignmentSlice';
import {RootState} from '../../../redux/store';
import InstructionsFlyout from './instructions/InstructionsFlyout';
import SortByMetaData from './doc-presentation-sequence/SortByMetaData';
import OptimizedSort from './doc-presentation-sequence/OptimizedSort';
import {setAvailableCriteria} from '../../../redux/slice/optimizedSortSlice';
import AddKeywords from './add-keywords/AddKeywords';
import PoolContainer from './pool-container/PoolContainer';
import {ISelectItem} from '../../../shared/types';
import {ArrToggle} from '../../common/arr-toggle/ArrToggle';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import DistributeDocuments from './edit-assignment/distribute-documents/DistributeDocuments';
import ManageReviewers from './edit-assignment/manage-reviewers/ManageReviewers';
import {
  convertEditAssignmentData,
  convertEditToCreate,
} from '../AssignmentsUtils';

interface TabData {
  label: string;
  disabled: boolean;
  content: React.ReactNode;
}

const cascadeInfo = () => (
  <>
    <p data-testid={`new-assignment-cascade-info1`}>
      {t('assignment.new.cascadeInfo1')}
    </p>
    <p data-testid={`new-assignment-cascade-info2`}>
      {t('assignment.new.cascadeInfo2')}
    </p>
  </>
);

type DocPresentationSequenceKey = 'optimizedSort' | 'sortByMetaData';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
  className?: string;
}

function TabPanel(props: Readonly<TabPanelProps>) {
  const {children, value, index, ...other} = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      className="tabpanel"
      {...other}
      data-testid={`new-assignment-tab-${index}`}
    >
      {value === index && (
        <div data-testid={`new-assignment-tab-child-${index}`}>{children}</div>
      )}
    </div>
  );
}

const NewAssignment: React.FC = () => {
  const [openManagePermissions, setOpenManagePermissions] =
    useState<boolean>(false);
  const [openMetadata, setOpenMetadata] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [nameError, setNameError] = useState<boolean>(false);
  const MAX_NAME_LENGTH = 250;
  const [isNameAtLimit, setIsNameAtLimit] = useState<boolean>(false);
  const [codingFormError, setCodingFormError] = useState<boolean>(false);
  const [openSelectSortCodingForms, setOpenSelectSortCodingForms] =
    useState<boolean>(false);
  const [isAddKeywords, setIsAddKeywords] = useState<boolean>(false);
  const [openInstructions, setOpenInstructions] = useState<boolean>(false);
  const [cascadeToggle, setCascadeToggle] = useState<boolean>(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    formData: assignmentFormData,
    listData: assignmentListData,
    selectedData,
    bulkAssignFlowData,
    backSourceTabIndex,
  } = useSelector((state: RootState) => state.assignment);

  const [tabValue, setTabValue] = useState(backSourceTabIndex ?? 0);
  const {assignmentId, assignmentGroupId, type, mode} = selectedData;

  const isBulkAssignFlow = bulkAssignFlowData.isBulkAssignFlow;
  const parentGroupIdFromState = bulkAssignFlowData.parentGroupId;
  const masterDocListIdFromState = bulkAssignFlowData.masterDocListId;
  const assignmentIdFromState = bulkAssignFlowData.assignmentId;
  const bulkActionIdFromState = bulkAssignFlowData.bulkActionId;

  const effectiveAssignmentId = isBulkAssignFlow
    ? assignmentIdFromState
    : assignmentId;
  const effectiveParentGroupId = isBulkAssignFlow
    ? parentGroupIdFromState
    : assignmentGroupId;

  const {data: createData, isLoading} = useCreateLandingData(
    `?AssignmentId=${effectiveAssignmentId}&IsAssignmentGroup=${type == 'assignmentGroup'}&ParentGroupId=${effectiveParentGroupId}`,
    mode === 'create'
  );
  const {data: editData, isLoading: editIsLoading} = useGetAssignmentGroupById(
    `?assignmentGroupId=${assignmentId}`,
    mode === 'edit' && type === 'assignmentGroup'
  );

  const {data: editAssignmentData, isLoading: isEditLoading} =
    useEditAssignmentById(
      `?AssignmentId=${assignmentId}`,
      mode === 'edit' && type === 'assignment'
    );

  const initialData = useMemo(
    () =>
      mode === 'edit' && type === 'assignmentGroup'
        ? convertEditToCreate(editData)
        : mode === 'edit' && type === 'assignment'
          ? convertEditAssignmentData(editAssignmentData)
          : createData,
    [createData, editAssignmentData, editData, mode, type]
  );

  const {mutate: createAssignment, reset: resetCreateAssignment} =
    useCreateAssignment();

  const {mutate: editAssignmentGroup, reset: resetEditAssignmentGroup} =
    useEditAssignmentGroup();

  const treeData = useSelector(
    (state: RootState) => state.assignment.assignmentTree
  );
  const parentIsCascadeEnabled = treeData?.find(
    (item) =>
      +item.id === assignmentFormData.assignmentModel.parentAssignmentGroupID
  )?.li_attr.isCascadeEnabled;

  const isCascadeEnabled = assignmentFormData.assignmentModel.isCascadeEnabled;

  useEffect(() => {
    return () => {
      dispatch(clearBackSourceTabIndex());
    };
  }, []);

  useEffect(() => {
    if (assignmentFormData.assignmentModel.selectCodingFormLists) {
      setCodingFormError(false);
    }
  }, [assignmentFormData]);

  useEffect(() => {
    if (initialData) {
      let initialCategorySeq = initialData?.assignmentData
        .docPresentationSorting.lstCategorySeq
        ? [...initialData.assignmentData.docPresentationSorting.lstCategorySeq]
        : [];

      const emailIdx = initialCategorySeq.findIndex(
        (item) => item.categoryName === 'Email Threads'
      );
      const familyIdx = initialCategorySeq.findIndex(
        (item) => item.categoryName === 'Family Members'
      );

      if (emailIdx > -1 && familyIdx > -1 && emailIdx < familyIdx) {
        const temp = initialCategorySeq[emailIdx];
        initialCategorySeq[emailIdx] = initialCategorySeq[familyIdx];
        initialCategorySeq[familyIdx] = temp;

        initialCategorySeq = initialCategorySeq.map((item, index) => ({
          ...item,
          seq: index + 1,
        }));
      }

      const listData = {
        assignedMetadata: initialData?.assignedMetadata,
        codingFormsList: initialData?.codingFormsList,
        classification: initialData?.classification,
        unAssignedMetadata: initialData?.unAssignedMetadata,
        additionalPreferences:
          initialData?.assignmentData.additionalPreferences,
        docPresentationSorting: {
          lstCategorySeq: initialCategorySeq,
          lstMetaDataFieldModel:
            initialData?.assignmentData.docPresentationSorting.lstMetaDataFieldModel
              ?.slice()
              .sort((a, b) => a.fieldName.localeCompare(b.fieldName)),
        },
        lstKeywordsModel: initialData?.assignmentData.lstKeywordsModel,
      };

      const isCascadeEnabled =
        initialData?.assignmentData.parentsIsCascadeEnabled || false;

      const formData = {
        selectedSeq: '',
        assignmentModel: {
          isAssignmentGroup: initialData?.assignmentData.isAssignmentGroup,
          assignmentID: isBulkAssignFlow
            ? Number(assignmentIdFromState)
            : initialData?.assignmentData.assignmentID,
          assignmentInstructions:
            initialData?.assignmentData.assignmentInstructions,
          assignmentName: initialData?.assignmentData.assignmentName,
          parentAssignmentGroupID: isBulkAssignFlow
            ? Number(parentGroupIdFromState)
            : initialData?.assignmentData.parentAssignmentGroupID,
          parentassignmentGroupName:
            initialData?.assignmentData.parentassignmentGroupName,
          isCascadeEnabled: initialData?.assignmentData.isCascadeEnabled,
          classificationLevelID:
            initialData?.assignmentData.classificationLevelID,
          parentsIsCascadeEnabled: isCascadeEnabled,
          codingFormID: initialData?.assignmentData.codingFormID,
          isActive: initialData?.assignmentData.isActive,
          priority: initialData?.assignmentData.priority,
          isDrawFromPool: initialData?.assignmentData.isDrawFromPool,
          drawFromPoolCount: initialData?.assignmentData.drawFromPoolCount,
          isKeepFamiliesTogether:
            initialData?.assignmentData.isKeepFamiliesTogether,
          isKeepEmailThreadsTogether:
            initialData?.assignmentData.isKeepEmailThreadsTogether,
          keepFamiliesTogetherCount:
            initialData?.assignmentData.keepFamiliesTogetherCount,
          range_2a: 0,
          additionalPreferences: {
            isShowMiniDocList:
              initialData?.assignmentData.additionalPreferences
                .isShowMiniDocList,
            isAllowPopoutPanels:
              initialData?.assignmentData.additionalPreferences
                .isAllowPopoutPanels,
            isAllowReviwerRemarks:
              initialData?.assignmentData.additionalPreferences
                .isAllowReviwerRemarks,
            isAllowFolderActions:
              initialData?.assignmentData.additionalPreferences
                .isAllowFolderActions,
            isShowDefaultViewTab:
              initialData?.assignmentData.additionalPreferences
                .isShowDefaultViewTab,
            isEnableHightlighting:
              initialData?.assignmentData.additionalPreferences
                .isEnableHightlighting,
            isExposeDocumentHistory:
              initialData?.assignmentData.additionalPreferences
                .isExposeDocumentHistory,
            isAllowAccesstoFullDoclist:
              initialData?.assignmentData.additionalPreferences
                .isAllowAccesstoFullDoclist,
            isShowAnalyticsPanel:
              initialData?.assignmentData.additionalPreferences
                .isShowAnalyticsPanel,
            isEnableRecommendations:
              initialData?.assignmentData.additionalPreferences
                .isEnableRecommendations,
            isAllowMetadataView:
              initialData?.assignmentData.additionalPreferences
                .isAllowMetadataView,
            isAllowCodingOutsideReviwerBatch:
              initialData?.assignmentData.additionalPreferences
                .isAllowCodingOutsideReviwerBatch,
            isAllowCodingStamps:
              initialData?.assignmentData.additionalPreferences
                .isAllowCodingStamps,
            isShowAssignmentProgress:
              initialData?.assignmentData.additionalPreferences
                .isShowAssignmentProgress,
            isAllowProductionView:
              initialData?.assignmentData.additionalPreferences
                .isAllowProductionView,
            isAllowSaveWithoutCompletion:
              initialData?.assignmentData.additionalPreferences
                .isAllowSaveWithoutCompletion,
            isEnableRedactions:
              initialData?.assignmentData.additionalPreferences
                .isEnableRedactions,
            isNearNativeView:
              initialData?.assignmentData.additionalPreferences
                .isNearNativeView,
            isPreCreatedTIFFImg:
              initialData?.assignmentData.additionalPreferences
                .isPreCreatedTIFFImg,
            isAllowManualSort:
              initialData?.assignmentData.additionalPreferences
                .isAllowManualSort,
            isAllowPDFPrinting:
              initialData?.assignmentData.additionalPreferences
                .isAllowPDFPrinting,
            isAllowNativeDownloads:
              initialData?.assignmentData.additionalPreferences
                .isAllowNativeDownloads,
            isShowEmailThreadMaptabAnalyticsPanel:
              initialData?.assignmentData.additionalPreferences
                .isShowEmailThreadMaptabAnalyticsPanel,
            isShowNearDuplicatetabAnalyticsPanel:
              initialData?.assignmentData.additionalPreferences
                .isShowNearDuplicatetabAnalyticsPanel,
            isShowFamilyMemberstabAnalyticsPanel:
              initialData?.assignmentData.additionalPreferences
                .isShowFamilyMemberstabAnalyticsPanel,
            isShowConceputallySimilartabAnalyticsPanel:
              initialData?.assignmentData.additionalPreferences
                .isShowConceputallySimilartabAnalyticsPanel,
            isShowRelatedDocstab:
              initialData?.assignmentData.additionalPreferences
                .isShowRelatedDocstab,
            isShowFoldersTab:
              initialData?.assignmentData.additionalPreferences
                .isShowFoldersTab,
            shouldCompleteOnApplyCodingStamp:
              initialData?.assignmentData.additionalPreferences
                .shouldCompleteOnApplyCodingStamp,
            isAutoSaveCodingForm:
              initialData?.assignmentData.additionalPreferences
                .isAutoSaveCodingForm,
            isShowAiPredictionHints:
              initialData?.assignmentData.additionalPreferences
                .isShowAiPredictionHints,
          },
          docPresentationSorting: {
            isCategory:
              initialData?.assignmentData.docPresentationSorting.isCategory,
            isMetadata:
              initialData?.assignmentData.docPresentationSorting.isMetadata,
            selectedMetaDataFieldId:
              initialData?.assignmentData.docPresentationSorting
                .selectedMetaDataFieldId,
            metaDataSeq:
              initialData?.assignmentData.docPresentationSorting.metaDataSeq,
            metaDataDirection:
              initialData?.assignmentData.docPresentationSorting
                .metaDataDirection,
            isImportList:
              initialData?.assignmentData.docPresentationSorting.isImportList,
            isAIScore:
              initialData?.assignmentData.docPresentationSorting.isAIScore,
            selectedModelAiScoreId:
              initialData?.assignmentData.docPresentationSorting
                .selectedModelAiScoreId,
            aiScoreModelDirection:
              initialData?.assignmentData.docPresentationSorting
                .aiScoreModelDirection,
            shouldDisplayEmailTogether:
              initialData?.assignmentData.docPresentationSorting
                .shouldDisplayEmailTogether,
            shouldDisplayFamilyTogether:
              initialData?.assignmentData.docPresentationSorting
                .shouldDisplayFamilyTogether,
            aiChkForFamilyTogether:
              initialData?.assignmentData.docPresentationSorting
                .aiChkForFamilyTogether,
            aiChkForEmailsTogether:
              initialData?.assignmentData.docPresentationSorting
                .aiChkForEmailsTogether,
            metadatChkForFamilyTogether:
              initialData?.assignmentData.docPresentationSorting
                .metadatChkForFamilyTogether,
            metadataChkForEmailsTogether:
              initialData?.assignmentData.docPresentationSorting
                .metadataChkForEmailsTogether,
          },
          selectCodingFormLists:
            initialData?.assignmentData.selectCodingFormLists ?? '[]',
          pageSize: initialData?.assignmentData.pageSize,
          pageNumber: initialData?.assignmentData.pageNumber,
          isAscending: initialData?.assignmentData.isAscending,
          sortingColumn: initialData?.assignmentData.sortingColumn,
          totalDocuments: initialData?.assignmentData.totalDocuments,
          unAssignedDocuments: initialData?.assignmentData.unAssignedDocuments,
          noOfDocToDistribute: initialData?.assignmentData.noOfDocToDistribute,
          assignmentKeywordIds: initialData?.assignmentData.assignmentKeywordIds
            ? initialData?.assignmentData.assignmentKeywordIds
            : (initialData?.assignmentData.lstKeywordsModel?.map((item) =>
                item.keywordID.toString()
              ) ?? []),
          assignmentMetaDataIds:
            initialData?.assignmentData.assignmentMetaDataIds,
          isTemporary: initialData?.assignmentData.isTemporary,
          isLockNeeded: initialData?.assignmentData.isLockNeeded,
          isAIEnabled: initialData?.assignmentData.isAIEnabled,
          distributeFamilyDocuments:
            initialData?.assignmentData.distributeFamilyDocuments,
          distributeEmailThreadDocuments:
            initialData?.assignmentData.distributeEmailThreadDocuments,
        },
        // Store bulk assign master doc list ID and bulk action ID separately
        bulkAssignMasterDocListId: isBulkAssignFlow
          ? Number(masterDocListIdFromState)
          : undefined,
        bulkActionId: isBulkAssignFlow
          ? Number(bulkActionIdFromState)
          : undefined,
      };

      setName(initialData?.assignmentData.assignmentName ?? '');
      setValue(
        initialData.assignmentData.docPresentationSorting.isCategory ? 0 : 1
      );
      dispatch(setListData(listData));
      dispatch(setFormData(formData));
      dispatch(
        setAvailableCriteria(
          initialData.assignmentData.docPresentationSorting
            .lstNotAssignedCategorySeq
        )
      );

      setCascadeToggle(initialData?.assignmentData.isCascadeEnabled ?? false);
    }
  }, [
    initialData,
    isBulkAssignFlow,
    parentGroupIdFromState,
    masterDocListIdFromState,
    assignmentIdFromState,
    bulkActionIdFromState,
  ]);

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
      name: `simple-tab-${index}`,
      'data-testid': `simple-tab-${index}`,
    };
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const tabData: TabData[] = [
    {
      label: t('assignment.edit.configuration'),
      disabled: false,
      content: <Box />,
    },
    {
      label: t('assignment.edit.manageReviewers'),
      disabled: false,
      content: <Box />,
    },
    {
      label: t('assignment.edit.distributeDocuments'),
      disabled: false,
      content: <DistributeDocuments />,
    },
  ];

  const [value, setValue] = useState(0);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const shortInfo = () => (
    <>
      <p data-testid={`new-assignment-tt0`}>
        {t('assignment.new.docSequence.tooltip.tt0')}
      </p>
      <br data-testid={`new-assignment-br`} />
      <p data-testid={`new-assignment-tt1`}>
        {t('assignment.new.docSequence.tooltip.tt1')}
      </p>
      <br data-testid={`new-assignment-br-btn`} />
      <p data-testid={`new-assignment-tt2`}>
        {t('assignment.new.docSequence.tooltip.tt2')}
      </p>
      <br data-testid={`new-assignment-br-button`} />
      <p data-testid={`new-assignment-tt3`}>
        {t('assignment.new.docSequence.tooltip.tt3')}
      </p>
      <br data-testid={`new-assignment-br-icon`} />
      <p data-testid={`new-assignment-tt4`}>
        {t('assignment.new.docSequence.tooltip.tt4')}
      </p>
      <ul data-testid={`new-assignment-tt5-tt6-tt7`}>
        <li data-testid={`new-assignment-tt5`}>
          {t('assignment.new.docSequence.tooltip.tt5')}
        </li>
        <li data-testid={`new-assignment-tt6`}>
          {t('assignment.new.docSequence.tooltip.tt6')}
        </li>
        <li data-testid={`new-assignment-tt7`}>
          {t('assignment.new.docSequence.tooltip.tt7')}
        </li>
      </ul>
      <p data-testid={`new-assignment-tt8`}>
        {t('assignment.new.docSequence.tooltip.tt8')}
      </p>
      <br data-testid={`new-assignment-br-link`} />
      <p data-testid={`new-assignment-tt9`}>
        {t('assignment.new.docSequence.tooltip.tt9')}
      </p>
      <br data-testid={`new-assignment-br-input`} />
    </>
  );

  const docPresentationSequenceTab: {
    id: DocPresentationSequenceKey;
    label: string;
    content: React.ReactNode;
  }[] = [
    {
      id: 'optimizedSort',
      label: t('assignment.new.docSequence.optimizedSort.tabName'),
      content: <OptimizedSort />,
    },
    {
      id: 'sortByMetaData',
      label: t('assignment.new.docSequence.sortByMetadata.tabName'),
      content: <SortByMetaData />,
    },
  ];

  const handleNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value.trim() == '') {
      setNameError(true);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const trimmedValue = rawValue.slice(0, MAX_NAME_LENGTH);

    setName(trimmedValue);
    setIsNameAtLimit(trimmedValue.length >= MAX_NAME_LENGTH);

    if (trimmedValue.trim() !== '') {
      const isValid = FIRST_LAST_NAME_REGEX.test(trimmedValue.trim());
      setNameError(!isValid);
    } else {
      setNameError(false);
    }
  };

  const handleIsActiveChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ): void => {
    dispatch(
      setFormData({
        ...assignmentFormData,
        assignmentModel: {
          ...assignmentFormData.assignmentModel,
          isActive: checked,
        },
      })
    );
  };

  const getSystemErrorMessage = () => {
    if (name.trim().length === 0 && nameError) {
      return t('assignment.new.nameRequiredError');
    }

    if (nameError) {
      return t('assignment.new.nameSpecialError');
    }

    if (isNameAtLimit) {
      return t('assignment.new.nameLengthError');
    }

    return '';
  };

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);

  const triggerToast = (message: string, status: ToastType[]) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  const handleSave = () => {
    if (
      type === 'assignment' &&
      (
        JSON.parse(assignmentFormData.assignmentModel.selectCodingFormLists) ??
        []
      ).length == 0
    ) {
      setCodingFormError(true);
      return;
    }

    const payload = {
      selectedSeq:
        assignmentListData.docPresentationSorting.lstCategorySeq
          .map((item) => item.categoryFieldId)
          .join(',') +
        (assignmentListData.docPresentationSorting.lstCategorySeq.length > 0
          ? ','
          : ''),
      assignmentModel: {
        ...assignmentFormData.assignmentModel,
        assignmentName: name.trim(),
        assignmentMetaDataIds:
          assignmentListData.assignedMetadata.map((item) =>
            item.key.toString()
          ) ?? [],
        assignmentKeywordIds:
          assignmentFormData.assignmentModel.assignmentKeywordIds.length > 0
            ? assignmentFormData.assignmentModel.assignmentKeywordIds
            : null,
        isCascadeEnabled:
          type === 'assignmentGroup' && !isCascadeEnabled
            ? cascadeToggle
            : assignmentFormData.assignmentModel.isCascadeEnabled,
        selectCodingFormLists:
          assignmentFormData.assignmentModel.selectCodingFormLists ?? '',
        docPresentationSorting: {
          ...assignmentFormData.assignmentModel.docPresentationSorting,
          isCategory: value == 0,
          isMetadata: value == 1,
        },
      },
      ...(isBulkAssignFlow && {
        bulkActionDataObj: {
          selectedAssignmentId: Number(assignmentIdFromState),
          bulkActionId: bulkActionIdFromState,
          bulkActionTypeCode: 'ASSIGN',
          bulkActionContextTypeCode: 'BULKASSIGN',
          bulkActionContextId: 0,
          masterDocListID: masterDocListIdFromState,
          actionType: 'ASSIGN',
          sourceType: 'DOCEXPLORER',
          isAdd: true,
        },
      }),
    };

    if (mode == 'create') {
      createAssignment(payload, {
        onSuccess: (data) => {
          if (data.updateStatus === 'DuplicateAssignment') {
            triggerToast(t('assignment.new.duplicateWarning'), [
              ToastType.Warning,
            ]);
          } else {
            triggerToast(
              type == 'assignment'
                ? t('assignment.new.addSuccessAssignment')
                : t('assignment.new.addSuccessAssignmentGroup'),
              [ToastType.Success]
            );
            setTimeout(() => {
              if (isBulkAssignFlow) {
                // Store parent group ID in Redux for ManageAssignment to use
                dispatch(
                  setSelectedData({
                    assignmentId: 0,
                    assignmentGroupId: Number(parentGroupIdFromState),
                    type: 'assignment',
                    mode: 'view',
                  })
                );
                dispatch(clearBulkAssignFlowData());
                navigate('/Assignment/ManageAssignment');
              } else {
                navigate(-1);
              }
              resetCreateAssignment();
            }, 1000);
          }
        },
        onError: (error) => {
          triggerToast(
            error?.message ?? t('codingForms.editor.errorSaveForm'),
            [ToastType.Error]
          );
        },
      });
    } else {
      if (mode === 'edit') {
        editAssignmentGroup(payload, {
          onSuccess: () => {
            triggerToast(
              type == 'assignment'
                ? t('assignment.edit.updateSuccessAssignment')
                : t('assignment.edit.editSuccessAssignmentGroup'),
              [ToastType.Success]
            );
            setTimeout(() => {
              navigate(-1);
              resetEditAssignmentGroup();
            }, 1000);
          },
          onError: (error) => {
            triggerToast(
              error?.message ?? t('codingForms.editor.errorSaveForm'),
              [ToastType.Error]
            );
          },
        });
      }
    }
  };

  const handlePriorityChange = (value: ISelectItem | null): void => {
    dispatch(setPriority(Number(value?.id ?? 1)));
  };

  const handleClassificationChange = (value: ISelectItem | null): void => {
    dispatch(setClassification(Number(value?.key ?? 0)));
  };

  const getClassificationData = () => {
    return assignmentListData.classification
      ? [...assignmentListData.classification]
      : [];
  };

  return (
    <Box className="new-assignment">
      <Box className="new-header-box">
        <Box className="header-title">
          <Typography
            className="typo-text headline3_size title-text"
            data-testid={`new-assignment-name`}
          >
            {type === 'assignment' &&
              mode == 'create' &&
              `${initialData?.assignmentData.parentassignmentGroupName ? initialData?.assignmentData.parentassignmentGroupName + ': ' : ''} ${t('assignment.new.newAssignment')}`}
            {type === 'assignment' &&
              mode == 'edit' &&
              t('assignment.edit.editAssignment')}
            {type === 'assignmentGroup' &&
              mode == 'create' &&
              t('assignment.new.newAssignmentGroupTitle')}
            {type === 'assignmentGroup' &&
              mode == 'edit' &&
              t('assignment.edit.editAssignmentGroupTitle')}
          </Typography>
        </Box>
        <Box className="header-button">
          <ArrButton
            title={t('assignment.new.backToManage')}
            dataTestId={'assignment.new.backToManage'}
            id={t('assignment.new.backToManage')}
            name={t('assignment.new.backToManage')}
            variant="secondary"
            size="small"
            startIcon={<LeftChevron className="new-nav-icon" />}
            onClick={() => {
              navigate(-1);
            }}
          >
            {t('assignment.new.backToManage')}
          </ArrButton>
          <ArrButton
            title={t('common.save')}
            dataTestId={'assignment-common.save'}
            id={t('common.save')}
            name={t('common.save')}
            variant="primary"
            size="small"
            onClick={handleSave}
            disabled={
              name.trim() == '' ||
              nameError ||
              (type == 'asssignment' && codingFormError)
            }
          >
            {t('common.save')}
          </ArrButton>
        </Box>
      </Box>
      {type === 'assignment' && mode == 'edit' && (
        <Box className="tab-header">
          <Tabs
            value={tabValue}
            onChange={handleChange}
            aria-label="assign user tabs"
            className="assign-user-tabs"
          >
            {tabData.map((tab, index) => (
              <Tab
                key={tab.label}
                label={tab.label}
                disabled={tab.disabled}
                className="assign-user-tab"
                {...a11yProps(index)}
              />
            ))}
          </Tabs>
        </Box>
      )}
      {tabValue === 0 && (
        <Box className="new-body">
          {isLoading || editIsLoading || isEditLoading ? (
            <ArrLoadingSpinner />
          ) : (
            <>
              <Box className="new-body-group first">
                <Box className="new-input">
                  <Box className="new-input-box">
                    <Box className="new-input-label">
                      <Typography
                        className="typo-text label1_size"
                        data-testid="percent-to-assign-text"
                      >
                        {type == 'assignment'
                          ? t('assignment.new.assignmentName')
                          : t('assignment.new.assignmentGroup')}
                      </Typography>
                      <ArrTooltipWrapper
                        title={t('assignment.new.assignmentNamePlaceholder')}
                        trigger={
                          <InfoIcon fontSize="medium" className="info-icon" />
                        }
                      >
                        {type == 'assignment'
                          ? t('assignment.new.assignmentNameInfo')
                          : t('assignment.new.assignmentGroupInfo')}
                      </ArrTooltipWrapper>
                    </Box>
                    <ArrTextField
                      fullWidth
                      className="text-box"
                      state={nameError || isNameAtLimit ? 'error' : 'enabled'}
                      sizeVariant="medium"
                      value={name}
                      onChange={handleNameChange}
                      onBlur={handleNameBlur}
                      showErrorIcon={nameError || isNameAtLimit}
                      supportText={getSystemErrorMessage()}
                      data-testid="assignment-name"
                      slotProps={{htmlInput: {maxLength: 250}}}
                      placeholder={
                        type == 'assignment'
                          ? t('assignment.new.assignmentNamePlaceholder')
                          : t('assignment.new.assignmentGroupNamePlaceholder')
                      }
                      dataTestId={
                        type == 'assignment'
                          ? t('assignment.new.assignmentNamePlaceholder')
                          : t('assignment.new.assignmentGroupNamePlaceholder')
                      }
                    />
                  </Box>
                  {type === 'assignment' && (
                    <Box className="coding-form-main">
                      <Box className="coding-form">
                        <Typography
                          className="typo-text label1_size"
                          data-testid="percent-to-assign-text"
                        >
                          {t('assignment.new.codingForm')}
                        </Typography>
                        <Box className="coding-form-button">
                          <ArrButton
                            title={t('assignment.new.codingFormButton')}
                            dataTestId={'assignment.new.codingFormButton'}
                            id={t('assignment.new.codingFormButton')}
                            name={t('assignment.new.codingFormButton')}
                            variant="primary"
                            size="small"
                            onClick={() => {
                              setOpenSelectSortCodingForms(true);
                            }}
                          >
                            {t('assignment.new.codingFormButton')}
                          </ArrButton>
                          {codingFormError && (
                            <Typography
                              className="typo-text body3_size coding-form-error error-text"
                              data-testid={`new-assignment-coding-from-required-error`}
                            >
                              {t('assignment.new.codingFromRequiredError')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Typography
                        className="typo-text body3_size selected-coding-form"
                        data-testid={`new-assignment-cf-text`}
                      >
                        {assignmentFormData?.assignmentModel
                          ?.selectCodingFormLists &&
                          JSON.parse(
                            assignmentFormData?.assignmentModel
                              ?.selectCodingFormLists
                          )
                            .map((value: any) => {
                              if (value.isDefault) {
                                return `${value.codingFormName} (Set as Default)`;
                              }
                              return value.codingFormName;
                            })
                            .join(', ')}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box className="new-input second">
                  <Box className="new-input-box">
                    {type === 'assignment' && mode === 'edit' && (
                      <Box className="assignment-active-section">
                        <Box className="new-input-label">
                          <Typography
                            className="typo-text label1_size"
                            data-testid="assignment-active-label"
                          >
                            {t('assignment.edit.assignmentActive')}
                          </Typography>
                        </Box>
                        <ArrToggle
                          value={assignmentFormData.assignmentModel.isActive}
                          onChange={handleIsActiveChange}
                          toggleSize="large"
                          dataTestId="assignment-active-toggle"
                          ariaLabel={t('assignment.edit.assignmentActive')}
                        />
                      </Box>
                    )}
                    <Box className="new-input-label">
                      <Typography
                        className="typo-text label1_size"
                        data-testid="percent-to-assign-text"
                      >
                        {type === 'assignment'
                          ? t('assignment.new.newAssignmentGroup')
                          : t('assignment.new.parentAssignmentGroup')}
                      </Typography>
                      <ArrTooltipWrapper
                        title={t('assignment.new.parentAssignmentGroup')}
                        trigger={
                          <InfoIcon fontSize="medium" className="info-icon" />
                        }
                      >
                        {t('assignment.new.parentAssignmentGroupInfo')}
                      </ArrTooltipWrapper>
                    </Box>
                    <ArrTextField
                      fullWidth
                      className="text-box"
                      state={'inactive'}
                      sizeVariant="medium"
                      value={
                        initialData?.assignmentData.parentassignmentGroupName
                      }
                      dataTestId="percent-to-assign-text"
                    />
                  </Box>
                  {type === 'assignment' && (
                    <Box className="classification">
                      <Box className="new-dropdown first-drop">
                        <Box className="new-input-label">
                          <Typography
                            className="typo-text label1_size"
                            data-testid="percent-to-assign-text"
                          >
                            {t('assignment.new.classification')}
                          </Typography>
                          <ArrTooltipWrapper
                            title={t('assignment.new.classification')}
                            trigger={
                              <InfoIcon
                                fontSize="medium"
                                className="info-icon"
                              />
                            }
                          >
                            {t('assignment.new.classificationInfo')}
                          </ArrTooltipWrapper>
                        </Box>
                        <ArrSingleSelectDropdown
                          selectedValues={
                            assignmentFormData.assignmentModel
                              .classificationLevelID
                              ? (getClassificationData().find(
                                  (item) =>
                                    item.key ===
                                    assignmentFormData.assignmentModel
                                      .classificationLevelID
                                ) ?? null)
                              : null
                          }
                          dropdownLabel={t('assignment.new.selectClass')}
                          dropdownItems={getClassificationData()}
                          dropdownItemKey="key"
                          dropdownItemDisplay="value"
                          sizeVariant="medium"
                          customWidth={18}
                          onSelectionChange={handleClassificationChange}
                          clearSelection
                          dataTestId="asd-select-class"
                        />
                      </Box>
                      <Box className="new-dropdown">
                        <Box className="new-input-label">
                          <Typography
                            className="typo-text label1_size"
                            data-testid="percent-to-assign-text"
                          >
                            {t('assignment.new.priority')}
                          </Typography>
                          <ArrTooltipWrapper
                            title={t('assignment.new.priority')}
                            trigger={
                              <InfoIcon
                                fontSize="medium"
                                className="info-icon"
                              />
                            }
                          >
                            {t('assignment.new.priorityInfo')}
                          </ArrTooltipWrapper>
                        </Box>
                        <ArrSingleSelectDropdown
                          selectedValues={
                            assignmentFormData.assignmentModel.priority
                              ? {
                                  id: assignmentFormData.assignmentModel
                                    .priority,
                                  value:
                                    assignmentFormData.assignmentModel.priority.toString(),
                                }
                              : {id: 1, value: '1'}
                          }
                          dropdownLabel={''}
                          dropdownItems={[
                            {id: 1, value: '1'},
                            {id: 2, value: '2'},
                            {id: 3, value: '3'},
                            {id: 4, value: '4'},
                            {id: 5, value: '5'},
                            {id: 6, value: '6'},
                            {id: 7, value: '7'},
                            {id: 8, value: '8'},
                            {id: 9, value: '9'},
                          ]}
                          dropdownItemKey="id"
                          dropdownItemDisplay="value"
                          sizeVariant="medium"
                          customWidth={9.5}
                          onSelectionChange={handlePriorityChange}
                          dataTestId="asd-select-values"
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
              <Box className="new-body-group">
                <Box className="new-input">
                  <Box className="new-input-label-main">
                    <Box className="new-input-label">
                      <Typography
                        className="typo-text subhead3_size"
                        data-testid="percent-to-assign-text"
                      >
                        {isCascadeEnabled
                          ? t('assignment.new.cascadeEnabled')
                          : t('assignment.new.cascadeDisabled')}
                      </Typography>
                      <ArrTooltipWrapper
                        title={t('assignment.new.cascadingSetting')}
                        trigger={
                          <InfoIcon
                            fontSize="medium"
                            className="info-icon-sub"
                          />
                        }
                      >
                        {cascadeInfo()}
                      </ArrTooltipWrapper>
                    </Box>
                    {type === 'assignmentGroup' && !parentIsCascadeEnabled && (
                      <ArrToggle
                        toggleSize="large"
                        checked={cascadeToggle}
                        dataTestId="assignment-group-toggle"
                        onChange={(e) => {
                          const value = e.target.checked;
                          setCascadeToggle(value);

                          dispatch(
                            setFormData({
                              ...assignmentFormData,
                              assignmentModel: {
                                ...assignmentFormData.assignmentModel,
                                isCascadeEnabled: value,
                              },
                            })
                          );
                        }}
                      />
                    )}
                  </Box>
                  <ArrButton
                    title={t('assignment.new.managePermission')}
                    dataTestId="assignment-manage-permissions"
                    id="manage-permissions"
                    name="manage-permissions"
                    variant="primary"
                    size="small"
                    onClick={() => setOpenManagePermissions(true)}
                  >
                    {t('assignment.new.managePermission')}
                  </ArrButton>
                  <PoolContainer />
                  <Box className="instructions-metadata-main">
                    <Box className="title-box configure-Fields-title">
                      <Typography
                        className="typo-text label1_size title"
                        data-testid="configureFieldsTitle-title"
                      >
                        {t(
                          'manageAssignments.poolContainer.configureFieldsTitle'
                        )}
                        &nbsp;
                      </Typography>
                      <ArrTooltipWrapper
                        trigger={
                          <InfoIcon
                            className="info-icon label1_size"
                            data-testid="configureFieldsTitle-info"
                          />
                        }
                      >
                        {t(
                          'manageAssignments.poolContainer.tooltip.configureFieldsTitle'
                        )}
                      </ArrTooltipWrapper>
                    </Box>
                    <Box className="instructions-metadata-row">
                      <ArrButton
                        title={t('assignment.new.instructions')}
                        dataTestId="assignment-Instructions"
                        id="Instructions"
                        name="Instructions"
                        variant="secondary"
                        size="small"
                        onClick={() => setOpenInstructions(true)}
                      >
                        {t('assignment.new.instructions')}
                      </ArrButton>
                      <ArrButton
                        title={t('manageAssignments.poolContainer.keywords')}
                        dataTestId="assignment-keywords-button"
                        id="keywords"
                        name="keywords"
                        variant="secondary"
                        size="small"
                        onClick={() => setIsAddKeywords(true)}
                        disabled={false}
                      >
                        {t('manageAssignments.poolContainer.keywords')}
                      </ArrButton>
                      <ArrButton
                        title={t('assignment.new.metadata')}
                        dataTestId="assignment-metadata"
                        id="metadata"
                        name="metadata"
                        variant="secondary"
                        size="small"
                        onClick={() => setOpenMetadata(true)}
                      >
                        {t('assignment.new.metadata')}
                      </ArrButton>
                    </Box>
                  </Box>
                </Box>
                <Box className="new-input second">
                  <Box className="heading">
                    <Typography
                      className="typo-text label1_size "
                      data-testid={`new-assignment-heading`}
                    >
                      {t('assignment.new.docSequence.heading')}
                    </Typography>
                    <ArrTooltipWrapper
                      title={t('assignment.new.docSequence.tooltip.heading')}
                      trigger={
                        <InfoIcon
                          fontSize="medium"
                          className="info-icon label1_size"
                        />
                      }
                    >
                      {shortInfo()}
                    </ArrTooltipWrapper>
                  </Box>
                  <Box className="sub-heading">
                    <Typography
                      className="typo-text body3_size"
                      data-testid={`new-assignment-sub-heading`}
                    >
                      {t('assignment.new.docSequence.subHeading')}
                    </Typography>
                  </Box>

                  <Box
                    className={`tabs-section ${parentIsCascadeEnabled && 'disbaled'}`}
                  >
                    <Tabs
                      value={value}
                      onChange={handleTabChange}
                      aria-label="edit user tabs"
                      className="object-tabs"
                    >
                      {docPresentationSequenceTab.map((tab) => (
                        <Tab
                          id={tab.id}
                          aria-controls={`tabpanel-${tab.id}`}
                          key={tab.id}
                          label={tab.label}
                          className="object-tab"
                          disabled={parentIsCascadeEnabled}
                        />
                      ))}
                    </Tabs>
                    {docPresentationSequenceTab.map((tab, index) => (
                      <TabPanel key={tab.id} value={value} index={index}>
                        {tab.content}
                      </TabPanel>
                    ))}
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
      )}
      {tabValue === 1 && <ManageReviewers />}
      {tabValue === 2 && <DistributeDocuments />}
      {openManagePermissions && (
        <ArrDrawer
          transition={transition_time_default}
          showHeader={false}
          showButton={false}
          scrollable
          isOpen={openManagePermissions}
          size="large"
          side="right"
          onClose={() => setOpenManagePermissions(false)}
        >
          <ManagePermissions
            onClose={() => {
              setOpenManagePermissions(false);
            }}
          />
        </ArrDrawer>
      )}
      {openInstructions && (
        <InstructionsFlyout
          isOpen={openInstructions}
          onClose={() => setOpenInstructions(false)}
        />
      )}
      {openMetadata && (
        <Metadata
          openDrawer={openMetadata}
          setOpenMetadataDrawer={() => setOpenMetadata(false)}
        />
      )}
      {openSelectSortCodingForms && (
        <SelectSortCodingForms
          showDrawer={openSelectSortCodingForms}
          handleDrawer={setOpenSelectSortCodingForms}
        />
      )}
      {isAddKeywords && (
        <AddKeywords
          showDrawer={isAddKeywords}
          handleDrawer={setIsAddKeywords}
        />
      )}
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={toastStatus}
          onClose={() => setShowToast(false)}
        />
      )}
    </Box>
  );
};

export default NewAssignment;
